<template>
  <v-dialog
    persistent
    scrollable
    v-model="dialog"
    max-width="700px"
    transition="dialog-transition"
  >
    <template #activator="{ on }">
      <v-btn
        icon
        small
        v-on="on"
        color="primary"
        class="text-none mb-1"
      >
        <v-icon>mdi-update</v-icon>
      </v-btn>
    </template>
    <v-card>
      <v-card-title primary-title>
        <span>
          {{ $t('production.rejectionTitle') }}
          - {{ production.shift }} | {{ machineLabel }} |
          {{ production.partname }}
        </span>
        <v-spacer></v-spacer>
        <v-btn
          icon
          @click="close"
          :disabled="saving"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="ma-0 pa-0">
        <template v-if="loading">
          <div class="text-center my-4">
            {{ $t('helper.loading') }}
          </div>
        </template>
        <div v-else>
          <v-expansion-panels
            accordion
            :value="hourlyData.length - 1"
            v-if="!rejectiontypeIsHourly"
          >
            <v-expansion-panel
              v-for="(data, index) in hourlyData"
              :key="index"
            >
              <v-expansion-panel-header
                :class="data.rejections.length ? 'error--text' : 'primary--text'"
              >
                <span>
                  <v-icon
                    left
                    class="primary--text"
                    :class="data.rejections.length ? 'error--text' : 'primary--text'"
                  >
                    mdi-clock-outline
                  </v-icon>
                  {{ data.displayhour }}
                </span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row no-gutters class="text-center">
                  <v-col cols="4">
                    <div class="body-2">
                      {{ $t('production.produced') }}
                    </div>
                    <div v-if="assetType === 'pieces'" class="text-uppercase title
                      font-weight-regular mb-2 info--text">
                      {{ data.produced }}
                    </div>
                    <v-row v-if="assetType === 'kgs'">
                      <v-col cols="6">
                        <div class="body-2">
                          {{ $t('production.qty')}}
                        </div>
                        <div class="text-uppercase title font-weight-regular mb-2 info--text">
                          {{ data.produced }}
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="body-2">
                          {{$t('production.kgs')}}
                        </div>
                        <div class="text-uppercase title font-weight-regular mb-2 info--text">
                          {{ (((data.netweight / 1000) / data.produced) * data.produced)
                            .toFixed(2) }}
                        </div>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="4">
                    <div class="body-2">
                      {{ $t('production.accepted') }}
                    </div>
                    <div v-if="assetType === 'pieces'" class="text-uppercase title
                      font-weight-regular mb-2 success--text">
                      {{ data.accepted }}
                    </div>
                    <v-row v-if="assetType === 'kgs'">
                      <v-col cols="6">
                        <div class="body-2">
                          {{ $t('production.qty')}}
                        </div>
                        <div class="text-uppercase title font-weight-regular mb-2 success--text">
                          {{ data.accepted }}
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="body-2">
                          {{$t('production.kgs')}}
                        </div>
                        <div class="text-uppercase title font-weight-regular mb-2 success--text">
                          {{ (((data.netweight / 1000) / data.produced) * data.accepted)
                            .toFixed(2) }}
                        </div>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="4">
                    <div class="body-2">
                      {{ $t('production.rejected') }}
                    </div>
                    <div v-if="assetType === 'pieces'" class="text-uppercase title
                      font-weight-regular mb-2 error--text">
                      {{ data.rejected }}
                    </div>
                    <v-row v-if="assetType === 'kgs'">
                      <v-col cols="6">
                        <div class="body-2">
                          {{ $t('production.qty')}}
                        </div>
                        <div class="text-uppercase title font-weight-regular mb-2 error--text">
                          {{ parseInt(data.rejected, 10) }}
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="body-2">
                          {{$t('production.kgs')}}
                        </div>
                        <div class="text-uppercase title font-weight-regular mb-2 error--text">
                          {{ (((data.netweight / 1000) / data.produced) *
                            parseInt(data.rejected, 10)).toFixed(2) }}
                        </div>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
                <v-data-table
                  dense
                  :headers="headers"
                  hide-default-footer
                  disable-pagination
                  :items="data.rejections"
                  v-if="data.rejections.length"
                  class="mb-2"
                >
                  <template #item.modifiedBy="{ item }">
                      <span v-if="item.editedBy">
                        {{ item.editedBy }}
                      </span>
                      <span v-else>
                        {{ item.createdBy }}
                      </span>
                    </template>
                  <!-- eslint-disable-next-line -->
                  <template #item.action="{ item, index }">
                    <edit-rejection
                      :hour="data.hour"
                      :productioninkgs="((data.netweight / 1000) / data.produced)"
                      :assetType="assetType"
                      :rejectionUnitWeight="partRejectionUnitWeight"
                      :rejection="item"
                      :updating="updating"
                      :updated="updated"
                      :rejectionReasons="rejectionReasons"
                      :acceptedQty="assetType === 'kgs'
                        ? (parseInt(((data.netweight / 1000)/ data.produced)
                        * (parseInt(data.produced, 10) - getRejected(data.rejections, index)), 10))
                        : (parseInt(data.produced, 10) - getRejected(data.rejections, index))"
                      @on-update="updateRejection"
                    />
                    <v-btn
                      icon
                      small
                      color="error"
                      :disabled="updating"
                      @click="deleteRejection(item)"
                    >
                      <v-icon>mdi-delete-outline</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
                <validation-observer
                  ref="form"
                  #default="{ handleSubmit }"
                >
                  <v-form
                    @submit.prevent="handleSubmit(() => addNewRejection(data))"
                  >
                    <v-row>
                      <v-col cols="12" sm="4">
                        <validation-provider
                          name="rejectedQuantity"
                          :rules="`required|numeric|min_value:1|max_value:${assetType === 'kgs' ?
                          ((data.netweight / 1000) / data.produced) * data.accepted : data.accepted}
                          `"
                          #default="{ errors }"
                        >
                          <v-text-field
                            dense
                            outlined
                            type="number"
                            :suffix="assetType === 'kgs'
                              ? $t('production.kilograms') : $t('production.pieces')"
                            :label="assetType === 'kgs' ? $t('production.weight')
                              : $t('production.quantity')"
                            :disabled="saving"
                            hide-details="auto"
                            v-model="data.newRejection.qty"
                            :error-messages="errors"
                            @input="handleRejectionQty"
                          ></v-text-field>
                        </validation-provider>
                      </v-col>
                      <v-col cols="12" sm="8">
                        <validation-provider
                          name="rejectionReason"
                          rules="required"
                          #default="{ errors }"
                        >
                          <v-autocomplete
                            outlined
                            dense
                            return-object
                            :label="$t('production.reason')"
                            :disabled="saving"
                            hide-details="auto"
                            item-text="reasonname"
                            item-value="reasonname"
                            :error-messages="errors"
                            :items="rejectionReasons"
                            v-model="data.newRejection.reason"
                          >
                            <template #selection="data">
                              {{ data.item.reasoncode }} | {{ data.item.reasonname }}
                            </template>
                            <template #item="data">
                              <v-list-item-content>
                                <v-list-item-title>
                                  {{ data.item.reasonname }}
                                </v-list-item-title>
                                <v-list-item-subtitle
                                  v-text="data.item.reasoncode"
                                ></v-list-item-subtitle>
                                <v-list-item-subtitle
                                  v-text="data.item.category"
                                ></v-list-item-subtitle>
                                <v-list-item-subtitle
                                  v-text="data.item.department"
                                ></v-list-item-subtitle>
                              </v-list-item-content>
                            </template>
                          </v-autocomplete>
                        </validation-provider>
                      </v-col>
                      <v-col cols="12" sm="4" v-if="assetType === 'pieces'">
                        <validation-provider
                          name="rejectedWeight"
                          :rules="`required|min_value:1`"
                          #default="{ errors }"
                        >
                          <v-text-field
                            dense
                            outlined
                            type="number"
                            :label="$t('production.weight')"
                            :disabled="saving"
                            hide-details="auto"
                            v-model="newRejectionWeight"
                            @input="handleWeightEdit"
                            :error-messages="errors"
                          ></v-text-field>
                        </validation-provider>
                      </v-col>
                      <v-col class="text-center" cols="12" sm="8" v-if="assetType === 'pieces'">
                        <div v-if="assetType === 'pieces'" class="title
                          font-weight-regular mb-2 info--text">
                          <!-- {{ `${data.newRejection.weight} g/piece` }} -->
                          {{ `${partRejectionUnitWeight} gm/piece` }}
                        </div>
                      </v-col>
                      <v-col cols="12">
                        <v-textarea
                          dense
                          rows="2"
                          outlined
                          hide-details
                          :disabled="saving"
                          :label="$t('production.remarksOptional')"
                          v-model="newWeightRemark"
                        ></v-textarea>
                      </v-col>
                    </v-row>
                    <v-row no-gutters>
                      <v-col cols="12">
                        <v-btn
                          block
                          type="submit"
                          color="primary"
                          :loading="saving"
                          class="text-none mt-2"
                        >
                          {{ $t('production.addRejection') }}
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-form>
                </validation-observer>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <div style="max-width: 99%;" v-else>
            <v-row no-gutters class="text-center">
              <v-col cols="4">
                <div class="body-2">
                  {{ $t('production.produced') }}
                </div>
                <div v-if="assetType === 'pieces'" class="text-uppercase title
                  font-weight-regular mb-2 info--text">
                  {{ productionShiftwise.produced }}
                </div>
                <v-row v-if="assetType === 'kgs'">
                  <v-col cols="6">
                    <div class="body-2">
                      {{ $t('production.qty')}}
                    </div>
                    <div class="text-uppercase title font-weight-regular mb-2 info--text">
                      {{ productionShiftwise.produced }}
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="body-2">
                      {{$t('production.kgs')}}
                    </div>
                    <div class="text-uppercase title font-weight-regular mb-2 info--text">
                      {{ (productionShiftwise.netweight / 1000).toFixed(2) }}
                    </div>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="4">
                <div class="body-2">
                  {{ $t('production.accepted') }}
                </div>
                <div v-if="assetType === 'pieces'" class="text-uppercase title
                  font-weight-regular mb-2 success--text">
                  {{ productionShiftwise.accepted }}
                </div>
                <v-row v-if="assetType === 'kgs'">
                  <v-col cols="6">
                    <div class="body-2">
                      {{ $t('production.qty')}}
                    </div>
                    <div class="text-uppercase title font-weight-regular mb-2 success--text">
                      {{ productionShiftwise.accepted }}
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="body-2">
                      {{$t('production.kgs')}}
                    </div>
                    <div class="text-uppercase title font-weight-regular mb-2 success--text">
                      {{ (((productionShiftwise.netweight / 1000) /
                        productionShiftwise.produced) * productionShiftwise.accepted)
                        .toFixed(2) }}
                    </div>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="4">
                <div class="body-2">
                  {{ $t('production.rejected') }}
                </div>
                <div v-if="assetType === 'pieces'" class="text-uppercase title
                  font-weight-regular mb-2 error--text">
                  {{ productionShiftwise.rejected }}
                </div>
                <v-row v-if="assetType === 'kgs'">
                  <v-col cols="6">
                    <div class="body-2">
                      {{ $t('production.qty')}}
                    </div>
                    <div class="text-uppercase title font-weight-regular mb-2 error--text">
                      {{ parseInt(productionShiftwise.rejected, 10) }}
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="body-2">
                      {{$t('production.kgs')}}
                    </div>
                    <div class="text-uppercase title font-weight-regular mb-2 error--text">
                      {{ (((productionShiftwise.netweight / 1000) / productionShiftwise.produced) *
                        parseInt(productionShiftwise.rejected, 10)).toFixed(2) }}
                    </div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-data-table
              dense
              :headers="headers"
              hide-default-footer
              disable-pagination
              :items="productionShiftwise.rejections"
              v-if="productionShiftwise.rejections.length"
              class="mb-2"
            >
              <template v-slot:item.modifiedBy="{ item }">
                  <span v-if="item.editedBy">
                    {{ item.editedBy }}
                  </span>
                  <span v-else>
                    {{ item.createdBy }}
                  </span>
                </template>
              <!-- eslint-disable-next-line -->
              <template v-slot:item.action="{ item, index }">
                <edit-rejection
                  :hour="productionShiftwise.hour"
                  :productioninkgs="((productionShiftwise.netweight / 1000) /
                    productionShiftwise.produced)"
                  :assetType="assetType"
                  :rejectionUnitWeight="partRejectionUnitWeight"
                  :rejection="item"
                  :updating="updating"
                  :updated="updated"
                  :rejectionReasons="rejectionReasons"
                    :acceptedQty="assetType === 'kgs'
                      ? parseInt(
                          ((productionShiftwise.netweight / 1000) /
                            productionShiftwise.produced) *
                          (parseInt(productionShiftwise.produced, 10) -
                            getRejected(productionShiftwise.rejections, index)),
                          10
                        )
                      : parseInt(productionShiftwise.produced, 10) -
                        getRejected(productionShiftwise.rejections, index)"
                  @on-update="updateMultiRejection"
                />
                <v-btn
                  icon
                  small
                  color="error"
                  :disabled="updating"
                  @click="deleteRejection(item)"
                >
                  <v-icon>mdi-delete-outline</v-icon>
                </v-btn>
              </template>
            </v-data-table>
            <validation-observer
              ref="rejectionForm"
              #default="{ handleSubmit }"
            >
              <v-form
                @submit.prevent="handleSubmit(() => addNewMultiRejection(
                  productionShiftwise.newRejection,
                ))"
                class="pa-2"
              >
                <v-row>
                  <v-col cols="12" sm="4">
                    <validation-provider
                      name="rejectedQuantity"
                      :rules="`required|numeric|min_value:1|max_value:${
                        assetType === 'kgs'
                          ? ((productionShiftwise.netweight / 1000) /
                              productionShiftwise.produced) *
                              productionShiftwise.accepted
                          : productionShiftwise.accepted
                      }`"
                      #default="{ errors }"
                    >
                      <v-text-field
                        dense
                        outlined
                        type="number"
                        :suffix="assetType === 'kgs'
                          ? $t('production.kilograms') : $t('production.pieces')"
                        :label="assetType === 'kgs' ? $t('production.weight')
                          : $t('production.quantity')"
                        :disabled="saving"
                        hide-details="auto"
                        v-model="productionShiftwise.newRejection.qty"
                        :error-messages="errors"
                        @input="handleRejectionQty"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" sm="8">
                    <validation-provider
                      name="rejectionReason"
                      rules="required"
                      #default="{ errors }"
                    >
                      <v-autocomplete
                        outlined
                        dense
                        return-object
                        :label="$t('production.reason')"
                        :disabled="saving"
                        hide-details="auto"
                        item-text="reasonname"
                        item-value="reasonname"
                        :error-messages="errors"
                        :items="rejectionReasons"
                        v-model="productionShiftwise.newRejection.reason"
                      >
                        <template #selection="productionShiftwise">
                          {{ productionShiftwise.item.reasoncode }} |
                          {{ productionShiftwise.item.reasonname }}
                        </template>
                        <template #item="productionShiftwise">
                          <v-list-item-content>
                            <v-list-item-title>
                              {{ productionShiftwise.item.reasonname }}
                            </v-list-item-title>
                            <v-list-item-subtitle>
                              {{ productionShiftwise.item.reasoncode }}
                            </v-list-item-subtitle>
                            <v-list-item-subtitle>
                              {{ productionShiftwise.item.category }}
                            </v-list-item-subtitle>
                            <v-list-item-subtitle>
                              {{ productionShiftwise.item.department }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" sm="4" v-if="assetType === 'pieces'">
                    <validation-provider
                      name="rejectedWeight"
                      :rules="`required|min_value:1`"
                      #default="{ errors }"
                    >
                      <v-text-field
                        dense
                        outlined
                        type="number"
                        :label="$t('production.weight')"
                        :disabled="saving"
                        hide-details="auto"
                        v-model="newRejectionWeight"
                        @input="handleWeightEdit"
                        :error-messages="errors"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col class="text-center" cols="12" sm="8" v-if="assetType === 'pieces'">
                    <div v-if="assetType === 'pieces'" class="title
                      font-weight-regular mb-2 info--text">
                      <!-- {{ `${data.newRejection.weight} g/piece` }} -->
                      {{ `${partRejectionUnitWeight} gm/piece` }}
                    </div>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      dense
                      rows="2"
                      outlined
                      hide-details
                      :disabled="saving"
                      :label="$t('production.remarksOptional')"
                      v-model="newWeightRemark"
                    ></v-textarea>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12">
                    <v-btn
                      block
                      type="submit"
                      color="primary"
                      :loading="saving"
                      class="text-none mt-2"
                    >
                      {{ $t('production.addRejection') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </validation-observer>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  mapActions,
  mapGetters,
  mapState,
  mapMutations,
} from 'vuex';
import EditRejection from './EditRejection.vue';

export default {
  name: 'AssignRejections',
  props: {
    production: {
      type: Object,
      required: true,
    },
    rejectiontypeIsHourly: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    EditRejection,
  },
  data() {
    return {
      hourlyData: [],
      headers: [
        { text: this.$t('production.qty'), value: 'quantity', width: 72 },
        { text: this.$t('production.reason'), value: 'reasonname' },
        { text: this.$t('production.remark'), value: 'remark', width: 96 },
        { text: this.$t('production.modifiedAt'), value: 'modifiedtimestamp' },
        { text: this.$t('production.modifiedBy'), value: 'modifiedBy' },
        { text: '', value: 'action', sortable: false },
      ],
      saving: false,
      loading: false,
      dialog: false,
      updating: false,
      updated: false,
      assetType: '',
      newRejectionWeight: 0,
      partRejectionUnitWeight: 1,
      newWeightRemark: '',
    };
  },
  watch: {
    dialog(val) {
      if (val) {
        this.getHourlyData();
      }
    },
  },
  computed: {
    ...mapState('productionLog', [
      'assetId',
      'rejectionReasons',
      'machines',
      'productionList',
    ]),
    ...mapGetters('productionLog', ['getHourStart', 'isClosureAsset']),
    ...mapGetters('user', ['fullName']),
    machineLabel() {
      return this.isClosureAsset
        ? (this.production.linename || this.production.displayName || this.production.machinename)
        : (this.production.displayName || this.production.machinename);
    },
    activeMachine() {
      if (!this.isClosureAsset) {
        return this.production.machinename;
      }
      const lineName = this.production.linename || this.production.displayName;
      const rejectionMachine = this.machines.find((mac) => (
        mac.linename === lineName && mac.isrejection === true
      ));
      return rejectionMachine ? rejectionMachine.machinename : this.production.machinename;
    },
    activeMachineRecord() {
      return this.machines.find((mac) => mac.machinename === this.activeMachine) || null;
    },
    productionShiftwise() {
      const result = this.groupRejectionDataForPlan(this.hourlyData);
      return result;
    },
  },
  methods: {
    ...mapActions('productionLog', [
      'fetchHourlyProduction',
      'fetchPartInfo',
      'reFetchProductionList',
      'addRejection',
      'fetchAssets',
    ]),
    ...mapActions('element', ['updateRecordById', 'deleteRecordById']),
    ...mapMutations('productionLog', ['setProductionList']),
    ...mapMutations('helper', ['setAlert']),
    getRejected(rejections, index) {
      return rejections
        .filter((r, i) => i !== index)
        .reduce((result, cur) => {
          result += parseInt(cur.quantity, 10);
          return result;
        }, 0);
    },
    handleRejectionQty(qty) {
      if (this.assetType === 'pieces') {
        this.newRejectionWeight = qty * this.partRejectionUnitWeight;
      }
    },
    handleWeightEdit() {
      this.newWeightRemark = `Weight edited by ${this.fullName}`;
    },
    async getHourlyData() {
      this.loading = true;
      const partInfo = await this.fetchPartInfo({ partname: this.production.partname });
      this.partRejectionUnitWeight = partInfo.netweight ? partInfo.netweight : 1;
      this.hourlyData = await this.fetchHourlyProduction({
        part: this.production.partname,
        shift: this.production.shift,
        planId: this.production.planid,
        machineName: this.activeMachine,
      });
      const assetType = await this.fetchAssets();
      this.assetType = assetType;
      this.loading = false;
    },
    async addNewRejection(data) {
      this.saving = true;
      const { qty, reason } = data.newRejection;
      let newqty = 0;
      if (this.assetType === 'kgs') {
        const newqtyinkgs = data.netweight / 1000;
        const newqtyincount = (qty / newqtyinkgs) * data.produced;
        const newqtycount = Math.round(newqtyincount);
        newqty = newqtycount;
      } else {
        newqty = qty;
      }
      const machine = this.activeMachineRecord;
      const { assetid, linename } = machine || {};
      const payload = {
        assetid,
        linename: linename || this.production.linename || this.production.displayName,
        planid: this.production.planid,
        partname: this.production.partname,
        componentclass: this.production.componentclass || '-',
        machinename: this.activeMachine,
        quantity: parseInt(newqty, 10),
        remark: this.newWeightRemark,
        rejectionWeight: parseInt(this.newRejectionWeight, 10),
        ...reason,
        timestamp: this.getHourStart(data.displayhour),
        timeType: 'BUSINESS_TIME',
        createdBy: this.fullName,
      };
      const id = await this.addRejection(payload);
      if (id) {
        this.updateHourlyStats(payload, newqty, data, id);
        if (this.isClosureAsset) {
          await this.reFetchProductionList();
        } else {
          this.updateShiftStats(newqty);
        }
        this.$nextTick(() => {
          this.$refs.form.forEach((f) => {
            f.reset();
          });
        });
      }
      this.newRejectionWeight = 0;
      this.newWeightRemark = '';
      this.saving = false;
    },
    updateHourlyStats(payload, qty, data, id) {
      const newRejection = {
        qty: '',
        reason: '',
        remark: '',
      };
      const index = this.hourlyData.findIndex((d) => d.hour === data.hour);
      const { rejections, rejected, produced } = this.hourlyData[index];
      const newRejectionValue = parseInt(rejected, 10) + parseInt(qty, 10);
      const rejectionsArray = [
        { ...payload, _id: id, modifiedtimestamp: this.$t('production.now') },
        ...rejections,
      ];
      const newData = {
        ...this.hourlyData[index],
        rejected: newRejectionValue,
        accepted: parseInt(produced, 10) - newRejectionValue,
        rejections: rejectionsArray,
        newRejection,
      };
      this.hourlyData.splice(index, 1, newData);
    },
    updateShiftStats(rejectedQty) {
      const shiftProduction = [...this.productionList];
      const lineName = this.production.linename || this.production.displayName;
      const index = shiftProduction.findIndex((prod) => (
        prod.shift === this.production.shift
        && (
          this.isClosureAsset
            ? prod.linename === lineName
            : prod.machinename === this.production.machinename
        )
        && prod.partname === this.production.partname
        && prod.planid === this.production.planid
      ));
      const rejected = parseInt(this.productionList[index].rejected, 10)
        + parseInt(rejectedQty, 10);
      const accepted = parseInt(this.productionList[index].produced, 10)
        - parseInt(rejected, 10);
      shiftProduction.splice(index, 1, {
        ...this.productionList[index],
        rejected,
        accepted,
      });
      this.setProductionList(shiftProduction);
    },
    async updateRejection(item) {
      this.updating = true;
      this.updated = false;
      const { id, hour } = item;
      let { payload } = item;
      const reason = this.rejectionReasons.find((r) => r.reasonname === payload.reasonname);
      const machine = this.activeMachineRecord;
      payload = {
        ...payload,
        reason,
        assetid: machine ? machine.assetid : payload.assetid,
        linename: machine ? machine.linename : (this.production.linename
          || this.production.displayName),
        machinename: this.activeMachine,
      };
      if (this.assetType === 'kgs') {
        const findhourIndex = this.hourlyData.findIndex((d) => d.hour === hour);
        const newqtyinkgs = this.hourlyData[findhourIndex].netweight / 1000;
        const newqtyincount = (payload.quantity / newqtyinkgs)
         * this.hourlyData[findhourIndex].produced;
        const newqty = Math.round(newqtyincount);
        payload.quantity = newqty;
      }
      const updated = await this.updateRecordById({
        elementName: 'rejection',
        id,
        payload,
      });
      if (updated) {
        const hourIndex = this.hourlyData.findIndex((d) => d.hour === hour);
        const { rejections } = this.hourlyData[hourIndex];
        // eslint-disable-next-line
        const updatedIndex = rejections.findIndex((s) => s._id === id);
        this.$set(rejections, updatedIndex, {
          ...rejections[updatedIndex],
          modifiedtimestamp: this.$t('production.now'),
          ...payload,
        });
        const hIndex = this.hourlyData.findIndex((d) => d.hour === hour);
        const { rejections: rej, produced } = this.hourlyData[hIndex];
        const rejected = rej.reduce((a, b) => a + (+b.quantity || 0), 0);
        const newData = {
          ...this.hourlyData[hIndex],
          rejected,
          accepted: +produced - rejected,
        };
        this.hourlyData.splice(hIndex, 1, newData);
        await this.reFetchProductionList();
        this.updated = true;
        this.setAlert({
          show: true,
          type: 'success',
          message: 'REJECTION_UPDATE',
        });
      } else {
        this.updated = false;
        this.setAlert({
          show: true,
          type: 'error',
          message: 'REJECTION_UPDATE',
        });
      }
      this.updating = false;
    },
    async deleteRejection(item) {
      const confirmDialog = this.$root && this.$root.$confirm;
      const confirmed = await confirmDialog.open(
        'Delete rejection',
        'Are you sure you want to delete this rejection entry?',
      );
      if (!confirmed) {
        return;
      }
      this.updating = true;
      this.updated = false;
      const rejectionIds = item.rejectionDetails && item.rejectionDetails.length
        ? item.rejectionDetails.map((detail) => detail._id)
        : [item._id];
      const deleted = await Promise.all(rejectionIds.map((id) => this.deleteRecordById({
        elementName: 'rejection',
        id,
      })));
      if (deleted.every(Boolean)) {
        this.setAlert({
          show: true,
          type: 'success',
          message: 'REJECTION_DELETE',
        });
        await this.getHourlyData();
        await this.reFetchProductionList();
        this.updated = true;
      } else {
        this.setAlert({
          show: true,
          type: 'error',
          message: 'ERROR_DELETE_RECORD',
        });
      }
      this.updating = false;
    },
    async updateMultiRejection(item) {
      this.updating = true;
      this.updated = false;
      const { id } = item;
      const { payload } = item;
      const groupedRejection = this.productionShiftwise.rejections
        .find((rejection) => rejection._id === id);
      const rejectionDetails = groupedRejection
        ? groupedRejection.rejectionDetails || []
        : [];
      const reason = this.rejectionReasons.find((r) => r.reasonname === payload.reasonname);
      const machine = this.activeMachineRecord;

      if (!groupedRejection || !rejectionDetails.length || !reason || !machine) {
        this.updated = false;
        this.updating = false;
        this.setAlert({
          show: true,
          type: 'error',
          message: 'REJECTION_UPDATE',
        });
        return;
      }

      const previousQty = this.getShiftRejectionDisplayQty(groupedRejection);
      const currentQty = Number(payload.quantity || 0);
      const requests = previousQty === currentQty
        ? this.getMultiReasonUpdateRequests(payload, reason, machine, rejectionDetails)
        : this.getMultiQuantityUpdateRequests(
          payload,
          reason,
          machine,
          rejectionDetails,
        );
      const updated = await Promise.all(requests);

      if (requests.length && updated.every(Boolean)) {
        await this.getHourlyData();
        await this.reFetchProductionList();
        this.updated = true;
        this.setAlert({
          show: true,
          type: 'success',
          message: 'REJECTION_UPDATE',
        });
      } else {
        this.updated = false;
        this.setAlert({
          show: true,
          type: 'error',
          message: 'REJECTION_UPDATE',
        });
      }
      this.updating = false;
    },
    getShiftRejectionDisplayQty(rejection) {
      if (this.assetType !== 'kgs') {
        return Number(rejection.quantity || 0);
      }

      const productionInKgs = (this.productionShiftwise.netweight / 1000)
        / this.productionShiftwise.produced;
      return parseInt((productionInKgs * Number(rejection.quantity || 0)).toFixed(2), 10);
    },
    getHourlyRejectionQty(hour, qty) {
      let quantity = Number(qty || 0);
      if (this.assetType === 'kgs') {
        const newqtyinkgs = hour.netweight / 1000;
        const newqtyincount = (quantity / newqtyinkgs) * hour.produced;
        quantity = Math.round(newqtyincount);
      }
      return parseInt(quantity, 10);
    },
    getDistributedRejectionWeight(totalWeight, totalQty, qty) {
      if (!Number(totalWeight) || !Number(totalQty)) {
        return 0;
      }

      return parseInt((Number(totalWeight) / Number(totalQty)) * Number(qty || 0), 10);
    },
    buildMultiUpdatePayload(
      payload,
      reason,
      machine,
      hour,
      qty,
      weight,
      useRecordQuantity = false,
    ) {
      return {
        ...payload,
        ...reason,
        assetid: machine.assetid,
        quantity: useRecordQuantity
          ? parseInt(qty, 10)
          : this.getHourlyRejectionQty(hour, qty),
        rejectionWeight: weight,
      };
    },
    buildMultiAddPayload(payload, reason, machine, hour, qty, weight) {
      return {
        assetid: machine.assetid,
        linename: machine.linename,
        planid: this.production.planid,
        partname: this.production.partname,
        componentclass: this.production.componentclass || '-',
        machinename: machine.machinename,
        quantity: this.getHourlyRejectionQty(hour, qty),
        remark: payload.remark,
        rejectionWeight: weight,
        ...reason,
        timestamp: this.getHourStart(hour.displayhour),
        timeType: 'BUSINESS_TIME',
        createdBy: this.fullName,
      };
    },
    getMultiReasonUpdateRequests(payload, reason, machine, rejectionDetails) {
      const totalQty = rejectionDetails.reduce(
        (sum, detail) => sum + Number(detail.quantity || 0),
        0,
      );

      return rejectionDetails.map((detail) => this.updateRecordById({
        elementName: 'rejection',
        id: detail._id,
        payload: this.buildMultiUpdatePayload(
          payload,
          reason,
          machine,
          detail,
          detail.quantity,
          this.getDistributedRejectionWeight(
            payload.rejectionWeight,
            totalQty,
            detail.quantity,
          ),
          true,
        ),
      }));
    },
    getMultiQuantityUpdateRequests(payload, reason, machine, rejectionDetails) {
      if (Number(payload.quantity || 0) <= 0) {
        return rejectionDetails.map((rejection) => this.deleteRecordById({
          elementName: 'rejection',
          id: rejection._id,
        }));
      }

      const detailsByHour = rejectionDetails.reduce((acc, detail) => {
        if (!acc[detail.hour]) {
          acc[detail.hour] = [];
        }
        acc[detail.hour].push(detail);
        return acc;
      }, {});
      const hourlyData = this.distributeShiftRejection(this.hourlyData, {
        qty: payload.quantity,
        ...reason,
        remark: payload.remark,
        ignoredRejectionIds: rejectionDetails.map((rejection) => rejection._id),
      });

      return hourlyData.reduce((requests, hour) => {
        const details = detailsByHour[hour.hour] || [];
        const [rejectionToUpdate, ...rejectionsToDelete] = details;
        const qty = Number(hour.newRejection.qty || 0);
        const weight = this.getDistributedRejectionWeight(
          payload.rejectionWeight,
          payload.quantity,
          qty,
        );

        if (qty > 0 && rejectionToUpdate) {
          requests.push(this.updateRecordById({
            elementName: 'rejection',
            id: rejectionToUpdate._id,
            payload: this.buildMultiUpdatePayload(
              payload,
              reason,
              machine,
              hour,
              qty,
              weight,
            ),
          }));
        } else if (qty > 0) {
          requests.push(this.addRejection(
            this.buildMultiAddPayload(payload, reason, machine, hour, qty, weight),
          ));
        }

        if (qty > 0) {
          rejectionsToDelete.forEach((rejection) => {
            requests.push(this.deleteRecordById({
              elementName: 'rejection',
              id: rejection._id,
            }));
          });
        }

        if (qty <= 0) {
          details.forEach((rejection) => {
            requests.push(this.deleteRecordById({
              elementName: 'rejection',
              id: rejection._id,
            }));
          });
        }

        return requests;
      }, []);
    },
    groupRejectionDataForPlan(data) {
      const result = data.reduce((acc, item) => {
        // Sum top-level values
        acc.produced += item.produced || 0;
        acc.accepted += item.accepted || 0;
        acc.rejected += item.rejected || 0;
        acc.netweight += Number(item.netweight || 0);

        // Merge rejection reasons
        (item.rejections || []).forEach((rej) => {
          const existing = acc.rejections
            .find((r) => r.reasonname === rej.reasonname && r.remark === rej.remark);

          if (existing) {
            existing.quantity += rej.quantity || 0;
            existing.rejectionWeight += rej.rejectionWeight || 0;
            existing.rejectionDetails.push({
              ...rej,
              hour: item.hour,
              displayhour: item.displayhour,
              produced: item.produced,
              netweight: item.netweight,
            });
          } else {
            acc.rejections.push({
              ...rej,
              quantity: rej.quantity || 0,
              rejectionWeight: rej.rejectionWeight || 0,
              rejectionDetails: [{
                ...rej,
                hour: item.hour,
                displayhour: item.displayhour,
                produced: item.produced,
                netweight: item.netweight,
              }],
            });
          }
        });

        return acc;
      }, {
        produced: 0,
        accepted: 0,
        rejected: 0,
        netweight: 0,
        rejections: [],
      });
      result.newRejection = {
        qty: '',
        reason: '',
        remark: '',
      };
      return result;
    },
    async addNewMultiRejection(shiftRejection) {
      const hourlyData = this.distributeShiftRejection(this.hourlyData, shiftRejection);
      const filteredData = hourlyData.filter((data) => data.newRejection.qty > 0);

      await Promise.all(filteredData.map((data) => this.addNewRejection(data)));
      this.$nextTick(() => {
        this.$refs.rejectionForm.reset();
      });
    },
    getHourAvailableRejectionQty(hour, ignoredRejectionIds = []) {
      const ignoredIds = new Set(ignoredRejectionIds);
      const rejected = (hour.rejections || []).reduce((sum, rejection) => {
        if (ignoredIds.has(rejection._id)) {
          return sum;
        }

        return sum + Number(rejection.quantity || 0);
      }, 0);
      const available = Math.max(Number(hour.produced || 0) - rejected, 0);

      if (this.assetType !== 'kgs') {
        return available;
      }

      if (!Number(hour.produced || 0)) {
        return 0;
      }

      const productionInKgs = (hour.netweight / 1000) / hour.produced;
      return productionInKgs * available;
    },
    distributeShiftRejection(hours, shiftRejection) {
      if (!Array.isArray(hours) || hours.length === 0) {
        console.error('Hours array is empty or invalid:', hours);
        return [];
      }

      if (!shiftRejection) {
        console.error('shiftRejection is undefined:', shiftRejection);
        return [];
      }

      const shiftQty = Number(shiftRejection.qty);

      if (!Number.isInteger(shiftQty) || shiftQty <= 0) {
        console.error('Invalid shift rejection quantity:', shiftRejection.qty);
        return [];
      }

      const availableHours = hours.map((item, index) => ({
        index,
        availableQty: Math.floor(this.getHourAvailableRejectionQty(
          item,
          shiftRejection.ignoredRejectionIds,
        )),
      }));
      const totalAvailable = availableHours.reduce(
        (sum, item) => sum + item.availableQty,
        0,
      );

      if (totalAvailable <= 0) {
        console.error('Total available production must be greater than zero.');
        return [];
      }

      if (shiftQty > totalAvailable) {
        console.error('Shift rejection quantity exceeds available production:', {
          shiftQty,
          totalAvailable,
        });
        return [];
      }

      const allocations = availableHours.map((item) => {
        const exactQty = (item.availableQty / totalAvailable) * shiftQty;
        const qty = Math.floor(exactQty);

        return {
          index: item.index,
          qty,
          maxQty: item.availableQty,
          remainder: exactQty - qty,
        };
      });

      const allocatedQty = allocations.reduce(
        (sum, item) => sum + item.qty,
        0,
      );

      let remaining = shiftQty - allocatedQty;

      allocations.sort((a, b) => {
        if (b.remainder !== a.remainder) {
          return b.remainder - a.remainder;
        }

        return a.index - b.index;
      });

      let allocationIndex = 0;

      while (remaining > 0) {
        const availableAllocations = allocations.filter((allocation) => (
          allocation.qty < allocation.maxQty
        ));

        if (!availableAllocations.length) {
          console.error('Allocation not found:', allocationIndex);
          break;
        }

        const allocation = availableAllocations[
          allocationIndex % availableAllocations.length
        ];

        allocation.qty += 1;
        allocationIndex += 1;
        remaining -= 1;
      }

      allocations.sort((a, b) => a.index - b.index);

      allocations.forEach((allocation) => {
        const hour = hours[allocation.index];

        if (!hour) {
          return;
        }

        this.$set(hour, 'newRejection', {
          qty: allocation.qty,
          reason: shiftRejection.reason || '',
          remark: shiftRejection.remark || '',
          weight: allocation.qty,
        });
      });
      return hours;
    },
    close() {
      this.dialog = false;
    },
  },
};
</script>
